import { createComponent } from '@lit/react'
import * as React from 'react'

import Component from '../../components/badge/badge.component.js'

const tagName = 'dap-ds-badge'

/**
 * `dap-ds-badge`
 * @summary A badge is a small status descriptor for UI elements.
 * @element dap-ds-badge
 * @title - Badge
 *
 * @slot - The content of the badge.
 * @slot icon - The icon of the badge.
 *
 * @csspart base - The main container of the badge.
 * @csspart icon - The icon of the badge.
 * @csspart content - The content of the badge.
 * @csspart icon-base - The base of the icon.
 *
 * @cssproperty --dds-badge-border-width - The width of the badge's border. Default is the design system's base border width.
 * @cssproperty --dds-badge-border-style - The style of the badge's border. Default is solid.
 * @cssproperty --dds-badge-border-radius - The border radius of the badge. Default is the design system's base radius.
 * @cssproperty --dds-badge-font-weight - The font weight of the badge text. Default is bold.
 * @cssproperty --dds-badge-line-height - The line height of the badge text. Default is 1.2.
 * @cssproperty --dds-badge-transition - The transition property for the badge. Default is 'all 0.2s ease-in-out'.
 *
 * @cssproperty --dds-badge-padding-sm - The padding of the small badge. Default is the design system's spacing-100 and spacing-200.
 * @cssproperty --dds-badge-padding-lg - The padding of the large badge. Default is the design system's spacing-100 and spacing-300.
 * @cssproperty --dds-badge-font-size-sm - The font size of the small badge. Default is the design system's font-xs.
 * @cssproperty --dds-badge-font-size-lg - The font size of the large badge. Default is the design system's font-sm.
 *
 * @cssproperty --dds-badge-neutral-border-color - The border color of the neutral badge. Default is the design system's border-neutral-base.
 * @cssproperty --dds-badge-neutral-background - The background color of the neutral badge. Default is the design system's background-neutral-medium.
 * @cssproperty --dds-badge-neutral-color - The text color of the neutral badge. Default is the design system's text-neutral-subtle.
 *
 * @cssproperty --dds-badge-brand-border-color - The border color of the brand badge. Default is the design system's border-brand-base.
 * @cssproperty --dds-badge-brand-background - The background color of the brand badge. Default is the design system's background-brand-medium.
 * @cssproperty --dds-badge-brand-color - The text color of the brand badge. Default is the design system's text-brand-subtle.
 *
 * @cssproperty --dds-badge-info-border-color - The border color of the info badge. Default is the design system's border-informative-base.
 * @cssproperty --dds-badge-info-background - The background color of the info badge. Default is the design system's background-informative-medium.
 * @cssproperty --dds-badge-info-color - The text color of the info badge. Default is the design system's text-informative-subtle.
 *
 * @cssproperty --dds-badge-positive-border-color - The border color of the positive badge. Default is the design system's border-positive-base.
 * @cssproperty --dds-badge-positive-background - The background color of the positive badge. Default is the design system's background-positive-medium.
 * @cssproperty --dds-badge-positive-color - The text color of the positive badge. Default is the design system's text-positive-subtle.
 *
 * @cssproperty --dds-badge-warning-border-color - The border color of the warning badge. Default is the design system's border-warning-subtle.
 * @cssproperty --dds-badge-warning-background - The background color of the warning badge. Default is the design system's background-warning-medium.
 * @cssproperty --dds-badge-warning-color - The text color of the warning badge. Default is the design system's text-warning-subtle.
 *
 * @cssproperty --dds-badge-negative-border-color - The border color of the negative badge. Default is the design system's border-negative-base.
 * @cssproperty --dds-badge-negative-background - The background color of the negative badge. Default is the design system's background-negative-medium.
 * @cssproperty --dds-badge-negative-color - The text color of the negative badge. Default is the design system's text-negative-subtle.
 */
const reactWrapper = createComponent({
  tagName,
  elementClass: Component,
  react: React,
  events: {},
  displayName: 'DapDSBadge',
})

export default reactWrapper
